import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { NGXLogger } from 'ngx-logger';
import { MatDialog } from '@angular/material/dialog';
import { catchError, combineLatest, map, Observable, OperatorFunction, throwError } from 'rxjs';
import { AuditTrailMappingsItem, EnrichedAuditTrailMappingsItem } from './audit-trail-mappings-item.model';
import { showErrorDialog } from '../../shared/utils';
import { UsersService } from '../../users/users.service';
import { CigpStaffService } from '../../cigp-staff/cigp-staff.service';
import { User } from '../../users/user.model';
import { CigpStaff } from '../../cigp-staff/cigp-staff.model';

function enrichAuditTrailMappingItem(
  item: AuditTrailMappingsItem,
  users: User[],
  staffs: CigpStaff[]
): EnrichedAuditTrailMappingsItem {
  const enriched = {
    ...item,
    client: users.find((u) => u.email === item.clientEmail) || null,
    cigpStaff: staffs.find((s) => s.email === item.cigpStaffEmail) || null,
    submitter: staffs.find((s) => s.email === item.submitterEmail) || null
  };

  return enriched;
}

function enrichObservableAuditTrailMappingsItems$(
  usersService: UsersService,
  cigpStaffService: CigpStaffService
): OperatorFunction<AuditTrailMappingsItem[], EnrichedAuditTrailMappingsItem[]> {
  return (source: Observable<AuditTrailMappingsItem[]>) =>
    combineLatest([source, usersService.getUsersFromCache$(), cigpStaffService.getStaffsFromCache$()]).pipe(
      map(([items, users, staffs]) => items.map((item) => enrichAuditTrailMappingItem(item, users, staffs))),
      map((items) => items.filter((r) => r !== null) as EnrichedAuditTrailMappingsItem[])
    );
}

@Injectable({
  providedIn: 'root'
})
export class AuditTrailMappingsService {
  private apiEndpoint = `${environment.apiEndpoint}/api/audit-trail/mappings`;

  constructor(
    private http: HttpClient,
    private logger: NGXLogger,
    private errorDialog: MatDialog,
    private usersService: UsersService,
    private cigpStaffService: CigpStaffService
  ) {}

  public getAuditTrailMappingsItems$(startDate: Date, endDate: Date): Observable<EnrichedAuditTrailMappingsItem[]> {
    return this.http
      .get<AuditTrailMappingsItem[]>(`${this.apiEndpoint}`, {
        params: this.computeQueryParams(startDate, endDate)
      })
      .pipe(
        catchError((error: any) => {
          this.logger.error(error);
          showErrorDialog(this.errorDialog, error);
          return throwError(() => error);
        }),
        enrichObservableAuditTrailMappingsItems$(this.usersService, this.cigpStaffService)
      );
  }

  private computeQueryParams(startDate: Date, endDate: Date) {
    const params: { [param: string]: string } = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };

    return params;
  }
}
